/**
 * Step Runner — Dispatches individual workflow steps.
 *
 * Routes sync actions (interactive/short/long) through CmdInterpreter
 * and async actions (background) through JobsApi with polling.
 *
 * @module gibbs/workflow/stepRunner
 */

import { getAction } from '../registry/index';

// ─── Timeout Defaults (ms) ─────────────────────────────────────────────────

// Timeouts bumped 2026-04-14:
//   short: 30s → 60s (BFD round-trips can exceed 30s on a busy server)
//   long: 120s → 600s (real docking runs 3-5 min per compound; the old
//     120s cap was timing out legitimate server work mid-dock, leaving
//     the server to keep computing after the client had already marked
//     the step failed)
const DEFAULT_TIMEOUTS = Object.freeze({
    interactive: 5_000,
    short: 60_000,
    long: 600_000,
    background: 3_600_000,
});

const POLL_INTERVAL_MS = 5_000;

// ─── Step Runner ────────────────────────────────────────────────────────────

/**
 * Run a single workflow step, dispatching based on ActionDef.runtime.
 *
 * @param {import('./Workflow.js').WorkflowNode} node — WorkflowNode with actionId, paramOverrides
 * @param {Object} resolvedInputs — Resolved input values from outputMapper
 * @param {Object} workspace — Workspace state for CmdInterpreter context
 * @param {Object} [options]
 * @param {AbortSignal} [options.abortSignal] — Signal to cancel execution
 * @param {number} [options.timeout] — Custom timeout in ms (overrides runtime default)
 * @param {function} [options.onJobProgress] — Callback for job polling progress
 * @returns {Promise<{success: boolean, result: object, error: string|null, outputs: object}>}
 */
export async function runStep(node, resolvedInputs, workspace, options = {}) {
    const actionDef = getAction(node.actionId);
    if (!actionDef) {
        return {
            success: false,
            result: null,
            error: `Unknown action: ${node.actionId}`,
            outputs: {},
        };
    }

    const runtime = actionDef.runtime || 'short';
    const timeoutMs = options.timeout || DEFAULT_TIMEOUTS[runtime] || DEFAULT_TIMEOUTS.short;

    // Race the dispatch against the timeout AND the abort signal so a user
    // cancel interrupts the step immediately, not at the next step boundary.
    // The orphaned dispatch promise still settles eventually but its result
    // is dropped by the time runStep has already returned.
    const timeout = createTimeout(timeoutMs, node.actionId);
    try {
        const racers = [
            dispatchStep(node, actionDef, resolvedInputs, workspace, options),
            timeout.promise,
        ];
        if (options.abortSignal) {
            racers.push(createAbortPromise(options.abortSignal));
        }
        const result = await Promise.race(racers);
        return result;
    } catch (err) {
        // Re-throw on abort so the executor can mark the step CANCELLED
        // (vs. FAILED) without going through the failure-decision prompt.
        if (options.abortSignal?.aborted) {
            throw err;
        }
        return {
            success: false,
            result: null,
            error: err.message || String(err),
            outputs: {},
        };
    } finally {
        // Whoever won, the timer is done. Harmless when the timeout is what fired.
        timeout.cancel();
    }
}

// ─── Dispatch Router ────────────────────────────────────────────────────────

/**
 * Route a step to sync or async dispatch based on runtime.
 */
async function dispatchStep(node, actionDef, resolvedInputs, workspace, options) {
    if (actionDef.runtime === 'background') {
        return dispatchAsync(node, actionDef, resolvedInputs, options);
    }
    return dispatchSync(node, actionDef, resolvedInputs);
}

// ─── Sync Dispatch (D3) ────────────────────────────────────────────────────

/**
 * Dispatch a sync action through CmdInterpreter.interpretActionPlan().
 * Constructs a single-action plan from the node's actionId, paramOverrides,
 * and resolvedInputs.
 */
async function dispatchSync(node, actionDef, resolvedInputs) {
    // Dynamic import to avoid circular dependency at module load time
    const { default: CmdInterpreter } = await import('../../cmds/CmdInterpreter');

    const plan = {
        intent: 'single_action',
        actions: [{
            actionId: node.actionId,
            params: { ...node.paramOverrides },
            resolvedInputs: { ...resolvedInputs },
        }],
    };

    const result = await CmdInterpreter.interpretActionPlan(plan);

    if (result.type === 'error' || (result.errors && result.errors.length > 0)) {
        const errorMsg = result.error
            || (result.errors && result.errors.join('; '))
            || 'Unknown execution error';
        return {
            success: false,
            result,
            error: errorMsg,
            outputs: {},
        };
    }

    // Nested-error detection. Many BMaps UserActions return
    // { errors: [...] } to signal failure rather than throwing or
    // setting a top-level error field. CmdInterpreter wraps these into
    // results.results[0].result without surfacing the errors at the
    // plan-level, so without this check the executor would treat a
    // failed load-protein-from-pdb as a silent success and carry on
    // with steps 2+ against an empty workspace.
    const firstActionResult = result.results?.[0]?.result;
    if (firstActionResult?.errors?.length > 0) {
        const nestedMsg = firstActionResult.errors
            .map((e) => (typeof e === 'string' ? e : e?.message || String(e)))
            .join('; ');
        return {
            success: false,
            result,
            error: `${actionDef.name || node.actionId}: ${nestedMsg}`,
            outputs: {},
        };
    }

    // Extract outputs from the first action result
    const outputs = extractSyncOutputs(result, actionDef);

    return {
        success: true,
        result,
        error: null,
        outputs,
    };
}

/**
 * Extract output values from a CmdInterpreter result.
 * The result shape is {type: 'executed', results: [{actionId, result, displayText}]}.
 * The `result` field within varies by action — we map ActionDef.outputs
 * to the result object as best we can.
 *
 * An ActionDef may declare `outputAliases: { <declaredOutput>: <resultKey> }` for
 * the common case where a handler is shared with the rest of the app and returns a
 * generic key while the ActionDef names the output for its domain. `energy-minimize`
 * declares `minimizedCompounds` and its handler returns `compounds`;
 * `dock-compound` declares `dockedPoses` and returns `compounds` too. Without the
 * alias the declared output never resolves, and since `promptTemplates` teaches the
 * planner to write `inputsFrom: {compounds: "step:2.minimizedCompounds"}`, the edge
 * the planner was told to build is exactly the one that could not resolve.
 */
function extractSyncOutputs(interpreterResult, actionDef) {
    const outputs = {};
    if (!interpreterResult.results || interpreterResult.results.length === 0) {
        return outputs;
    }

    const actionResult = interpreterResult.results[0].result;
    if (!actionResult) return outputs;

    const aliases = actionDef.outputAliases || {};

    // If the action defines outputs, try to map them from the result
    for (const outputDef of actionDef.outputs) {
        // The declared name wins when the handler actually produced it; the alias
        // is a fallback, not an override, so a handler that starts returning the
        // declared name needs no registry change.
        const resultKey = actionResult[outputDef.name] !== undefined
            ? outputDef.name
            : aliases[outputDef.name];
        if (resultKey && actionResult[resultKey] !== undefined) {
            outputs[outputDef.name] = actionResult[resultKey];
        }
    }

    // If no named outputs matched but there's a result, store as '_raw'
    if (Object.keys(outputs).length === 0 && actionResult != null) {
        outputs.rawResult = actionResult;
    }

    return outputs;
}

// ─── Async Dispatch (D4) ───────────────────────────────────────────────────

/**
 * Dispatch a background action via JobsApi:
 * 1. Look up ActionDef.jobType
 * 2. Build job request from resolvedInputs + paramOverrides
 * 3. Call postJob()
 * 4. Poll getJobs() every 5s until succeeded/failed
 * 5. On success: loadJobResult() and return
 * 6. On failure: return error
 */
async function dispatchAsync(node, actionDef, resolvedInputs, options = {}) {
    const { postJob, loadJobResult } = await import('../../jobman/JobsApi');

    if (!actionDef.jobType) {
        return {
            success: false,
            result: null,
            error: `Action "${node.actionId}" has runtime 'background' but no jobType defined`,
            outputs: {},
        };
    }

    // Build and submit the job
    const jobrequest = { ...node.paramOverrides, ...resolvedInputs };
    const stash = { nodeId: node.id, actionId: node.actionId };

    const { results: jobCreated, error: postError } = await postJob({
        jobtype: actionDef.jobType,
        jobrequest,
        stash,
    });

    if (postError || !jobCreated?.jobid) {
        return {
            success: false,
            result: jobCreated,
            error: postError || 'Failed to submit job — no jobid returned',
            outputs: {},
        };
    }

    const jobid = jobCreated.jobid;

    // Poll until completion
    const pollResult = await pollForCompletion(jobid, options);

    if (!pollResult.success) {
        return {
            success: false,
            result: pollResult.job,
            error: pollResult.error,
            outputs: {},
        };
    }

    // Load full results on success
    const { results: loadedResult, error: loadError } = await loadJobResult(jobid);

    if (loadError || !loadedResult) {
        return {
            success: false,
            result: loadedResult,
            error: loadError || 'Failed to load job results',
            outputs: {},
        };
    }

    // Check if loading results revealed a failure
    if (loadedResult.state === 'failed') {
        return {
            success: false,
            result: loadedResult,
            error: 'Job failed when retrieving results',
            outputs: {},
        };
    }

    const outputs = extractAsyncOutputs(loadedResult, actionDef);

    return {
        success: true,
        result: loadedResult,
        error: null,
        outputs,
    };
}

/**
 * Poll getJobs() at POLL_INTERVAL_MS until the job reaches
 * 'succeeded' or 'failed'. Respects abortSignal.
 *
 * Issue #922 fix (2026-05-15): when the abort signal fires while we
 * are polling a background job, fire-and-forget `deleteJob(jobid)` so
 * the server-side work actually stops. Without this, cancelling a
 * docking workflow leaves jobman computing for minutes after the
 * client has already marked the step cancelled.
 */
async function pollForCompletion(jobid, options = {}) {
    const { getJobs, deleteJob } = await import('../../jobman/JobsApi');
    const { abortSignal, onJobProgress } = options;

    // Helper: tell jobman to stop this job. Fire-and-forget — we do
    // not block the abort path on the delete call (it could hang or
    // 404 if the job already settled).
    const tellServerToStop = () => {
        deleteJob(jobid).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn(
                `[stepRunner] deleteJob(${jobid}) failed after abort:`,
                err?.message
            );
        });
    };

    // Polling loop — each iteration must wait for the prior poll to return.
    /* eslint-disable no-await-in-loop */
    while (true) {
        // Check abort before each poll
        if (abortSignal?.aborted) {
            tellServerToStop();
            return { success: false, job: null, error: 'Step execution was aborted' };
        }

        await sleep(POLL_INTERVAL_MS);

        // Check abort after sleeping
        if (abortSignal?.aborted) {
            tellServerToStop();
            return { success: false, job: null, error: 'Step execution was aborted' };
        }

        const { results: jobs, error: pollError } = await getJobs();

        if (pollError) {
            return { success: false, job: null, error: `Polling error: ${pollError}` };
        }

        const job = jobs?.find((j) => j.jobid === jobid);

        if (!job) {
            return { success: false, job: null, error: `Job ${jobid} not found during polling` };
        }

        if (onJobProgress) {
            onJobProgress({ jobid, state: job.state });
        }

        if (job.state === 'succeeded') {
            return { success: true, job, error: null };
        }

        if (job.state === 'failed') {
            return {
                success: false,
                job,
                error: `Job failed: ${job.stash?.error || job.state}`,
            };
        }

        // Still 'starting' or 'running' — continue polling
    }
    /* eslint-enable no-await-in-loop */
}

/**
 * Extract output values from a loadJobResult response.
 */
function extractAsyncOutputs(loadedResult, actionDef) {
    const outputs = {};
    const jobResults = loadedResult.jobResults;
    if (!jobResults) return outputs;

    for (const outputDef of actionDef.outputs) {
        if (jobResults[outputDef.name] !== undefined) {
            outputs[outputDef.name] = jobResults[outputDef.name];
        }
    }

    if (Object.keys(outputs).length === 0 && jobResults != null) {
        outputs.rawResult = jobResults;
    }

    return outputs;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Create a cancellable promise that rejects after the given timeout.
 *
 * Cancellable because the loser of the race has to be cleaned up. A `long` action
 * carries a 600s timeout, so a step that completed in a second used to leave that
 * timer armed for the remaining ten minutes and then reject into nothing — an
 * unhandled rejection per completed long step, and in Node a process that will not
 * exit, which is why a passing test of one of those steps hung the runner.
 *
 * @returns {{promise: Promise<never>, cancel: function(): void}}
 */
function createTimeout(ms, actionId) {
    let timer = null;
    const promise = new Promise((_, reject) => {
        timer = setTimeout(() => {
            reject(new Error(`Step "${actionId}" timed out after ${ms}ms`));
        }, ms);
    });
    return {
        promise,
        cancel: () => {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
        },
    };
}

/**
 * Create a promise that rejects when the given AbortSignal aborts.
 * The error message is checked by the executor to distinguish a user
 * cancel from a genuine step failure.
 */
const ABORT_ERROR_MESSAGE = 'Step execution was aborted';

function createAbortPromise(abortSignal) {
    return new Promise((_, reject) => {
        if (abortSignal.aborted) {
            reject(new Error(ABORT_ERROR_MESSAGE));
            return;
        }
        abortSignal.addEventListener(
            'abort',
            () => reject(new Error(ABORT_ERROR_MESSAGE)),
            { once: true },
        );
    });
}

/**
 * Sleep for a given number of milliseconds.
 */
function sleep(ms) {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
}
